import React, { FC, useMemo, useState, useRef } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { FormControl, useFormField } from "@/components/ui/form";
import { cn } from "@/lib/helper/utils";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";

function CustomButton({
  openCalendar,
  value,
  placeholder,
  onFocus,
  onBlur,
}: {
  openCalendar: () => void;
  value: string;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  const { error } = useFormField();

  return (
    <Button
      variant="outline"
      type="button"
      onClick={openCalendar}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(
        "w-full justify-start gap-[6px] text-left font-normal text-foreground text-base border-input h-12 !px-[6px]",
        "transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        error &&
          "ring-destructive/20 border-destructive focus-visible:ring-destructive"
      )}
    >
      <FaCalendarAlt className="h-5 w-5 text-[#8A8A8A]" />
      {value ? (
        <span className="h-5">{value}</span>
      ) : (
        <span className="h-5 text-transparent">{placeholder || "تاریخ"}</span>
      )}
    </Button>
  );
}

type DatePickerInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label: string;
  className?: string;
  labelClassName?: string;
};

const DatePickerInput: FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder,
  label,
  className,
  labelClassName,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get error from form field context
  const { error } = useFormField();

  const date = useMemo(
    () => new DateObject(value?.toString() ?? "").convert(persian, persian_fa),
    [value]
  );

  const selectedItem = React.useMemo(() => {
    return value && value !== "" ? date : null;
  }, [value, date]);

  React.useEffect(() => {
    setHasValue(Boolean(selectedItem && selectedItem.isValid));
  }, [selectedItem]);

  const isLabelFloating = isFocused || hasValue || isOpen;

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const labelClasses = cn(
    "absolute right-3 transition-all duration-200 ease-in-out pointer-events-none z-10",
    "text-foreground",
    isLabelFloating
      ? "top-0 -translate-y-1/2 bg-background px-1 text-sm font-medium"
      : "top-1/2 -translate-y-1/2 right-[35px]",
    error && isLabelFloating && "text-destructive",
    labelClassName
  );

  return (
    <div className={cn("relative", className)}>
      <DatePicker
        calendar={persian}
        locale={persian_fa}
        value={value !== "" ? date : ""}
        onChange={(date) => {
          if (onChange) {
            onChange(
              date?.isValid
                ? date?.convert(gregorian, gregorian_en).format("YYYY-MM-DD")
                : ""
            );
          }
        }}
        onOpen={() => handleOpenChange(true)}
        onClose={() => handleOpenChange(false)}
        render={(value: string, openCalendar: () => void) => {
          return (
            <FormControl>
              <CustomButton
                openCalendar={openCalendar}
                value={value}
                placeholder={placeholder}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </FormControl>
          );
        }}
        format="YYYY/MM/DD"
        containerStyle={{ width: "100%" }}
      />

      {/* Floating Label */}
      <label
        className={labelClasses}
        onClick={() => buttonRef.current?.focus()}
      >
        {label}
      </label>
    </div>
  );
};

export default DatePickerInput;
